/**
 * Report Viewer Component
 * 
 * Displays evaluation reports with markdown content, scores, and export options.
 * Implements FRRP02 requirements for report accessibility.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    Alert,
    Skeleton,
    IconButton,
    Tooltip,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    CircularProgress,
    TextField
} from '@mui/material';
import {
    GetApp as DownloadIcon,
    FileCopy as CopyIcon,
    Refresh as RefreshIcon,
    PictureAsPdf as PdfIcon,
    Assessment as RubricIcon,
    ExpandMore as ExpandMoreIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Star as StarIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    BarChart as BarChartIcon,
    School as SchoolIcon,
    Psychology as PsychologyIcon,
    Business as BusinessIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseClient';

import { useUser } from '../../contexts/UserContext';
import {
    getPresentationReport,
    exportReportAsPDF,
    copyToClipboard,
    downloadTextFile,
    getScoreColor,
    getScoreGrade,
} from '../../utils/api/reportingApi';
import { evaluateAllCategoriesConsolidated } from '../../utils/api/consolidatedPipelineApi';

// Define interfaces locally
interface Report {
    generatedAt?: { seconds: number } | Date;
    markdownContent?: string;
    overallScore?: number;
    [key: string]: any;
}

interface Presentation {
    id: string;
    description: string;
    tenantId: string;
    status?: string;
    evaluationStatus?: string;
    finalReport?: any;
    [key: string]: any;
}

interface EvaluationResult {
    id: string;
    presentationId: string;
    tenantId: string;
    category: string;
    key: string;
    overallScore: number;
    categoryFeedback: string;
    confidence: number;
    evaluatedAt: string;
}

interface CriterionResult {
    id: string;
    presentationId: string;
    tenantId: string;
    category: string;
    categoryKey: string;
    criterionName: string;
    score: number;
    feedback: string;
    strengths: string[];
    areasForImprovement: string[];
    recommendations: string[];
    evaluatedAt: string;
}

const ExpandableText = ({ text, maxLength = 512, variant = "body2", showReadMore = false }: { text: string, maxLength?: number, variant?: any, showReadMore?: boolean }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text || text.length === 0) {
        return (
            <Typography variant={variant} color="text.secondary" fontStyle="italic">
                No content available
            </Typography>
        );
    }

    const shouldTruncate = text.length > maxLength;
    const displayText = expanded || !shouldTruncate
        ? text
        : text.substring(0, maxLength) + '...';

    return (
        <Box>
            <Typography variant={variant} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {displayText}
            </Typography>
            {shouldTruncate && (
                <Button
                    size="small"
                    onClick={() => setExpanded(!expanded)}
                    sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
                >
                    {expanded ? 'Show Less' : showReadMore ? 'Read More' : 'Show More'}
                </Button>
            )}
        </Box>
    );
};

const calculateOverallScore = (evaluationResults: EvaluationResult[], criterionResults: CriterionResult[]) => {
    // Try to get from evaluation results first
    if (evaluationResults.length > 0) {
        const validCategoryScores = evaluationResults
            .map(result => {
                // Use provided overallScore or calculate from criteria
                let score = result.overallScore || 0;
                if (!score) {
                    const categoryCriteria = criterionResults.filter(c => c.category === result.category);
                    if (categoryCriteria.length > 0) {
                        const validScores = categoryCriteria
                            .map(c => c.score)
                            .filter(s => typeof s === 'number' && !isNaN(s));
                        if (validScores.length > 0) {
                            score = validScores.reduce((sum, s) => sum + s, 0) / validScores.length;
                        }
                    }
                }
                return score;
            })
            .filter(score => score > 0);

        if (validCategoryScores.length > 0) {
            return validCategoryScores.reduce((sum, score) => sum + score, 0) / validCategoryScores.length;
        }
    }

    // Fallback: calculate from all criterion results
    if (criterionResults.length > 0) {
        const validScores = criterionResults
            .map(c => c.score)
            .filter(score => typeof score === 'number' && !isNaN(score));

        if (validScores.length > 0) {
            return validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
        }
    }

    return 0;
};

const ScoreCard = ({ title, score, maxScore = 5, color = 'primary', icon, confidence, metrics }: {
    title: string,
    score?: number,
    maxScore?: number,
    color?: string,
    icon?: React.ReactNode,
    confidence?: number,
    metrics?: { label: string, value: string | number, subtext?: string, color?: string }[]
}) => {
    // Ensure we have a valid score
    const validScore = typeof score === 'number' && !isNaN(score) ? score : 0;

    // Always display as fraction for consistency
    const percentage = (validScore / maxScore) * 100;
    const displayScore = `${validScore.toFixed(1)}/${maxScore}`;

    const colorValue = getScoreColor(validScore, maxScore);
    const muiColor = (['primary', 'secondary', 'error', 'info', 'success', 'warning', 'default'].includes(colorValue)
        ? colorValue
        : 'primary') as "primary" | "secondary" | "error" | "info" | "success" | "warning" | "default";

    return (
        <Card sx={{ height: '100%', position: 'relative' }}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    {icon && <Box sx={{ mr: 1, color: 'text.secondary' }}>{icon}</Box>}
                    <Typography variant="h6" color="textSecondary">
                        {title}
                    </Typography>
                </Box>
                <Typography variant="h3" color={muiColor} sx={{ mb: 1 }}>
                    {displayScore}
                </Typography>
                <Chip
                    label={`Grade: ${getScoreGrade(validScore, maxScore)}`}
                    color={muiColor === 'default' ? undefined : (muiColor as any)}
                    size="small"
                    sx={{ mb: 1 }}
                />
                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, percentage))}
                    color={muiColor === 'default' ? undefined : (muiColor as any)}
                    sx={{ height: 8, borderRadius: 1, mb: metrics ? 2 : 0 }}
                />

                {metrics && metrics.length > 0 && (
                    <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: metrics.length > 1 ? '1fr 1fr' : '1fr', gap: 1 }}>
                        {metrics.map((metric, idx) => (
                            <Box key={idx} sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {metric.label}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight="bold" color={metric.color || "text.primary"}>
                                    {metric.value}
                                </Typography>
                                {metric.subtext && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                        {metric.subtext}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {confidence && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Confidence: {(confidence * 100).toFixed(0)}%
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};


const ReportViewer: React.FC = () => {
    const { presentationId } = useParams<{ presentationId: string }>();
    const navigate = useNavigate();
    const { userProfile } = useUser();

    const [report, setReport] = useState<Report | null>(null);
    const [presentation, setPresentation] = useState<Presentation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTabCategory, setActiveTabCategory] = useState('Presentation Structure');
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [copyContent, setCopyContent] = useState('');
    const [snackbar, setSnackbar] = useState<{ open: boolean, message: string, severity: 'success' | 'info' | 'warning' | 'error' }>({ open: false, message: '', severity: 'info' });
    const [regenerating, setRegenerating] = useState(false);
    const [evaluationResults, setEvaluationResults] = useState<EvaluationResult[]>([]);
    const [criterionResults, setCriterionResults] = useState<CriterionResult[]>([]);

    const isAdmin = userProfile?.role === 'tenantAdmin' || userProfile?.role === 'globalAdmin';

    useEffect(() => {
        if (presentationId && userProfile?.tenantId) {
            loadPresentationData();
            loadReport();
            setupRealTimeListeners();
        }
    }, [presentationId, userProfile?.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle error state based on available data
    useEffect(() => {
        console.log('🔍 Error state check:', {
            loading,
            hasReport: !!report,
            evaluationResultsCount: evaluationResults.length,
            criterionResultsCount: criterionResults.length
        });

        if (!loading && !report) {
            const hasEvaluationData = evaluationResults.length > 0 || criterionResults.length > 0;
            console.log('📊 Has evaluation data:', hasEvaluationData);

            if (!hasEvaluationData) {
                console.log('❌ Setting error - no data available');
                setError('The evaluation report for this presentation is not ready yet. The analysis may still be in progress.');
            } else {
                console.log('✅ Clearing error - evaluation data available');
                // We have evaluation data, so clear any error
                setError(null);
            }
        } else if (report) {
            console.log('✅ Report available, clearing any errors');
            setError(null);
        }
    }, [loading, report, evaluationResults, criterionResults]);

    const loadPresentationData = async () => {
        try {
            if (!userProfile?.tenantId || !presentationId) return;

            console.log('🎪 Fetching presentation data from Firestore for ID:', presentationId);

            // Query presentations collection directly
            const presentationDoc = doc(db, 'presentations', presentationId);
            const presentationSnapshot = await getDoc(presentationDoc);

            if (presentationSnapshot.exists()) {
                const presentationData = presentationSnapshot.data() as Presentation;
                console.log('🎯 Fetched presentation data:', presentationData);

                // Verify tenant access
                if (presentationData.tenantId === userProfile.tenantId) {
                    setPresentation(presentationData);
                    console.log('✅ Presentation data loaded successfully');

                    // Extract evaluation data from presentation document
                    extractEvaluationDataFromPresentation(presentationData);
                } else {
                    console.warn('❌ Presentation belongs to different tenant');
                }
            } else {
                console.warn('❌ Presentation document not found');
            }
        } catch (error) {
            console.error('Error loading presentation data:', error);
        }
    };

    const extractEvaluationDataFromPresentation = (presentationData: Presentation) => {
        try {
            console.log('📊 Extracting evaluation data from presentation document');

            const finalReport = presentationData.finalReport;
            if (!finalReport) {
                console.log('⚠️ No finalReport found in presentation');
                return;
            }

            // Extract evaluation results for each category
            const extractedEvaluationResults: EvaluationResult[] = [];
            const extractedCriterionResults: CriterionResult[] = [];

            // Map category keys to display names
            const categoryMap: Record<string, string> = {
                'structure': 'Presentation Structure',
                'soft_skills': 'Soft Skills',
                'knowledge': 'Solution Knowledge',
                'speaker_analysis': 'Delivery & Presence',
                'delivery_analysis': 'Delivery & Presence'
            };

            // Process each category in detailed_evaluations
            const detailedEvals = finalReport.detailed_evaluations || {};

            // Dynamically extract all categories
            Object.entries(detailedEvals).forEach(([key, value]) => {
                const categoryData = value as any;
                if (categoryData) {
                    // Determine display name (use mapping if available, otherwise format key)
                    const displayName = categoryMap[key] || key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

                    // Add to evaluationResults
                    extractedEvaluationResults.push({
                        id: `${presentationId}_${key}`,
                        presentationId: presentationId!,
                        tenantId: presentationData.tenantId,
                        category: displayName,
                        key: key, // Store original key
                        overallScore: categoryData.overall_score || 0,
                        categoryFeedback: categoryData.category_feedback || '',
                        confidence: categoryData.confidence || 0,
                        evaluatedAt: categoryData.timestamp || new Date().toISOString()
                    });

                    // Process individual criteria
                    const criteriaScores = categoryData.criteria_scores || [];
                    criteriaScores.forEach((criterion: any, index: number) => {
                        extractedCriterionResults.push({
                            id: `${presentationId}_${key}_${index}`,
                            presentationId: presentationId!,
                            tenantId: presentationData.tenantId,
                            category: displayName,
                            categoryKey: key, // Store original key
                            criterionName: criterion.criterion_name,
                            score: criterion.score || 0,
                            feedback: criterion.feedback || '',
                            strengths: criterion.strengths || [],
                            areasForImprovement: criterion.areas_for_improvement || [],
                            recommendations: criterion.recommendations || [],
                            evaluatedAt: categoryData.timestamp || new Date().toISOString()
                        });
                    });
                }
            });

            // Sort results to ensure consistent order (mapped categories first, then alphabetical)
            const mappedOrder = ['Presentation Structure', 'Soft Skills', 'Solution Knowledge'];
            extractedEvaluationResults.sort((a, b) => {
                const indexA = mappedOrder.indexOf(a.category);
                const indexB = mappedOrder.indexOf(b.category);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return a.category.localeCompare(b.category);
            });

            // Update active tab if needed
            if (extractedEvaluationResults.length > 0 && !extractedEvaluationResults.some(r => r.category === activeTabCategory)) {
                setActiveTabCategory(extractedEvaluationResults[0].category);
            }

            console.log(`✅ Extracted ${extractedEvaluationResults.length} evaluation results`);
            console.log(`✅ Extracted ${extractedCriterionResults.length} criterion results`);

            setEvaluationResults(extractedEvaluationResults);
            setCriterionResults(extractedCriterionResults);

        } catch (error) {
            console.error('Error extracting evaluation data:', error);
        }
    };

    const setupRealTimeListeners = () => {
        if (!userProfile?.tenantId || !presentationId) return () => { };

        console.log('🔥 Setting up real-time listener for presentation document');

        // Real-time listener for presentation document
        const presentationDocRef = doc(db, 'presentations', presentationId!);

        const unsubscribePresentation = onSnapshot(presentationDocRef, (snapshot) => {
            if (snapshot.exists()) {
                const presentationData = snapshot.data() as Presentation;
                console.log('🔥 Real-time update: Presentation data changed', {
                    status: presentationData.status,
                    evaluationStatus: presentationData.evaluationStatus,
                    hasFinalReport: !!presentationData.finalReport
                });

                // Verify tenant access
                if (presentationData.tenantId === userProfile.tenantId) {
                    setPresentation(presentationData);

                    // Re-extract evaluation data when presentation updates
                    extractEvaluationDataFromPresentation(presentationData);
                }
            }
        }, (error) => {
            console.error('Error in real-time listener:', error);
        });

        // Cleanup function
        return () => {
            console.log('🧹 Cleaning up real-time listener');
            unsubscribePresentation();
        };
    };

    const loadReport = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await getPresentationReport(presentationId!);

            // Debug: Log presentation data
            console.log('🎯 Presentation data received:', result.presentation);

            if (result.success) {
                console.log('📋 Report data:', result.report);
                console.log('🎪 Presentation details from report:', result.presentation);
                setReport(result.report);
                // Don't override presentation data from direct Firestore fetch
                if (!presentation && result.presentation) {
                    setPresentation(result.presentation);
                }
            } else {
                // Handle case when Firebase function is not available or report not found
                if (result.error === 'Report not found') {
                    console.log('Report not yet generated for presentation:', presentationId);
                    // Don't set error immediately - let the real-time listeners populate data first
                    // We'll check if we have evaluation data and only show error if truly nothing is available
                } else {
                    console.warn('Firebase function error:', result.error);
                    setError('Report generation service is currently unavailable. Please try again later or contact support if the issue persists.');
                }
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load report');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyMarkdown = async () => {
        try {
            const buildMarkdownFromStructuredReport = (r: any): string => {
                if (!r || typeof r !== 'object') return '';

                const overallScore = typeof r.overall_score === 'number' ? r.overall_score : undefined;
                const categoryScores = r.category_scores && typeof r.category_scores === 'object' ? r.category_scores : {};
                const detailed = r.detailed_evaluations && typeof r.detailed_evaluations === 'object' ? r.detailed_evaluations : {};

                const formatCategoryName = (key: string) =>
                    key.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                let md = `# Evaluation Report\n\n`;
                if (typeof overallScore === 'number') md += `## Overall Score\n\n- **Overall:** ${overallScore.toFixed(1)}/5\n\n`;

                const categoryKeys = Object.keys(detailed);
                if (categoryKeys.length === 0) return md.trim();

                md += `## Detailed Evaluation\n\n`;

                for (const catKey of categoryKeys) {
                    const cat = detailed[catKey] || {};
                    const catName = formatCategoryName(catKey);
                    const catScore = typeof cat.overall_score === 'number' ? cat.overall_score : (typeof categoryScores?.[catKey] === 'number' ? categoryScores[catKey] : undefined);

                    md += `### ${catName}\n\n`;
                    if (typeof catScore === 'number') md += `- **Score:** ${catScore.toFixed(1)}/5\n`;
                    if (cat.category_feedback) md += `- **Feedback:** ${String(cat.category_feedback).trim()}\n`;
                    md += `\n`;

                    const criteria = Array.isArray(cat.criteria_scores) ? cat.criteria_scores : [];
                    if (criteria.length > 0) {
                        md += `#### Criteria\n\n`;
                        for (const c of criteria) {
                            const name = c?.criterion_name ? String(c.criterion_name).trim() : 'Unnamed criterion';
                            const score = typeof c?.score === 'number' ? c.score : 0;
                            const feedback = c?.feedback ? String(c.feedback).trim() : '';
                            md += `- **${name}**: ${score.toFixed(1)}/5${feedback ? ` — ${feedback}` : ''}\n`;
                        }
                        md += `\n`;
                    }
                }

                return md.trim();
            };

            // Prefer already-loaded markdown, but if it's very short (common fallback), build from structured report.
            const rawMarkdown = (report as any)?.markdownContent ?? (report as any)?.markdown_content ?? '';
            const rawIsUsable = typeof rawMarkdown === 'string' && rawMarkdown.trim().length > 200;
            const structuredMarkdown = buildMarkdownFromStructuredReport(report);
            const chosenMarkdown = rawIsUsable ? rawMarkdown : structuredMarkdown;
            const hasNonWhitespace = typeof chosenMarkdown === 'string' && chosenMarkdown.trim().length > 0;

            if (!hasNonWhitespace) {
                showSnackbar('No markdown content available for this report yet. Please try again after the report finishes generating.', 'warning');
                return;
            }

            setCopyContent(chosenMarkdown);
            setShowCopyDialog(true);

            const wordCount = chosenMarkdown.split(/\s+/).filter(Boolean).length;
            showSnackbar(`Markdown content ready! ${wordCount} words available.`, 'success');
        } catch (err) {
            console.error('Copy markdown error:', err);
            showSnackbar('Failed to get markdown content. Please try again.', 'error');
        }
    };

    const handleCopyToClipboard = async () => {
        const success = await copyToClipboard(copyContent);
        setShowCopyDialog(false);

        if (success) {
            showSnackbar('Report copied to clipboard!', 'success');
        } else {
            showSnackbar('Failed to copy to clipboard', 'error');
        }
    };

    const handleDownloadMarkdown = () => {
        const filename = `evaluation_report_${presentationId}_${new Date().toISOString().split('T')[0]}.md`;
        downloadTextFile(copyContent, filename, 'text/markdown');
        setShowCopyDialog(false);
        showSnackbar('Report downloaded!', 'success');
    };

    const handleExportSpecificPage = async (pageId: string) => {
        try {
            // Show loading message
            showSnackbar('Generating PDF report... This may take a few moments.', 'info');

            const result = await exportReportAsPDF(presentationId!);

            if (result.success) {
                if (result.openedInNewWindow) {
                    showSnackbar('PDF report opened in new window! Check your pop-up blocker if you don\'t see it.', 'success');
                } else if (result.downloadUrl) {
                    showSnackbar('PDF generated successfully. Opening in new window...', 'success');
                    // Fallback: open URL in new tab
                    window.open(result.downloadUrl, '_blank');
                } else {
                    showSnackbar(result.message || 'PDF export completed.', 'success');
                }

                // If there's a note (like fallback text file), show it
                if (result.note) {
                    setTimeout(() => {
                        showSnackbar(result.note, 'warning');
                    }, 2000);
                }
            } else {
                showSnackbar(result.error || 'Failed to export PDF', 'error');
            }
        } catch (err) {
            console.error('PDF export error:', err);
            showSnackbar('Failed to export PDF. Please try again later.', 'error');
        }
    };

    const handleExportPDF = async () => {
        try {
            // Show loading message
            showSnackbar('Generating PDF report... This may take a few moments.', 'info');

            const result = await exportReportAsPDF(presentationId!);

            if (result.success) {
                if (result.openedInNewWindow) {
                    showSnackbar('PDF report opened in new window! Check your pop-up blocker if you don\'t see it.', 'success');
                } else if (result.downloadUrl) {
                    showSnackbar('PDF generated successfully. Opening in new window...', 'success');
                    // Fallback: open URL in new tab
                    window.open(result.downloadUrl, '_blank');
                } else {
                    showSnackbar(result.message || 'PDF export completed.', 'success');
                }

                // If there's a note (like fallback text file), show it
                if (result.note) {
                    setTimeout(() => {
                        showSnackbar(result.note, 'warning');
                    }, 2000);
                }
            } else {
                showSnackbar(result.error || 'Failed to export PDF', 'error');
            }
        } catch (err) {
            console.error('PDF export error:', err);
            showSnackbar('Failed to export PDF. Please try again later.', 'error');
        }
    };

    const handleRegenerateReport = async () => {
        try {
            setRegenerating(true);

            // Regenerate pipeline output by re-running the consolidated evaluation (V2).
            // This is the closest available "regenerate" operation without relying on a missing backend callable.
            if (!presentationId) {
                showSnackbar('Missing presentation ID', 'error');
                return;
            }

            showSnackbar('Re-running evaluation to regenerate report... This may take a few minutes.', 'info');
            await evaluateAllCategoriesConsolidated(presentationId);

            showSnackbar('Evaluation re-run started/completed. Reloading report...', 'success');
            await loadPresentationData();
            await loadReport();
        } catch (err) {
            showSnackbar('Failed to regenerate report', 'error');
        } finally {
            setRegenerating(false);
        }
    };

    const handleViewRubricDetails = () => {
        navigate(`/presentations/${presentationId}/rubric`);
    };

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width="60%" height={60} />
                <Skeleton variant="rectangular" width="100%" height={200} sx={{ mt: 2 }} />
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {[1, 2, 3].map(i => (
                        <Grid size={{ xs: 12, md: 4 }} key={i}>
                            <Skeleton variant="rectangular" height={150} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    // Check if we have evaluation data even without a formal report
    const hasEvaluationData = evaluationResults.length > 0 || criterionResults.length > 0;

    if (error && !hasEvaluationData) {
        const isReportNotReady = error.includes('not ready yet') || error.includes('not found');
        const hasPartialResults = false; // We already know there's no data

        return (
            <Box sx={{ p: 3 }}>
                <Alert severity={isReportNotReady ? "info" : "error"} sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        {isReportNotReady ? 'Report Not Ready' : 'Service Error'}
                    </Typography>
                    <Typography variant="body2">
                        {error}
                    </Typography>
                    {hasPartialResults && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                            Some evaluation data is available below.
                        </Typography>
                    )}
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
                    <Button
                        onClick={() => navigate(`/presentations/${presentationId}`)}
                        startIcon={<ArrowBackIcon />}
                        variant="outlined"
                    >
                        Back to Presentation
                    </Button>
                    <Button onClick={loadReport} startIcon={<RefreshIcon />} variant="contained">
                        Try Again
                    </Button>
                </Box>

                {/* Show partial results if available */}
                {hasPartialResults && (
                    <>
                        {/* Score Overview */}
                        {evaluationResults.length > 0 && (
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
                                    Partial Results Available
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 3 }}>
                                        <ScoreCard
                                            title="Overall Score"
                                            score={calculateOverallScore(evaluationResults, criterionResults)}
                                            maxScore={5}
                                            color="primary"
                                            icon={<StarIcon />}
                                        />
                                    </Grid>
                                    {evaluationResults.map((result) => {
                                        const getCategoryIcon = (category: string) => {
                                            switch (category) {
                                                case 'Presentation Structure': return <SchoolIcon />;
                                                case 'Soft Skills': return <PsychologyIcon />;
                                                case 'Solution Knowledge': return <BusinessIcon />;
                                                default: return <BarChartIcon />;
                                            }
                                        };

                                        const categoryCriteria = criterionResults.filter(c => c.category === result.category);
                                        let categoryScore = result.overallScore || 0;

                                        if (!categoryScore && categoryCriteria.length > 0) {
                                            const validScores = categoryCriteria
                                                .map(c => c.score)
                                                .filter(score => typeof score === 'number' && !isNaN(score));

                                            if (validScores.length > 0) {
                                                categoryScore = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
                                            }
                                        }

                                        return (
                                            <Grid size={{ xs: 12, md: 3 }} key={result.id}>
                                                <ScoreCard
                                                    title={result.category}
                                                    score={categoryScore}
                                                    maxScore={5}
                                                    color={getScoreColor(categoryScore, 5)}
                                                    icon={getCategoryIcon(result.category)}
                                                    confidence={result.confidence}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                        {/* Show simplified tabbed view for available results when no formal report */}
                        {(evaluationResults.length > 0 || criterionResults.length > 0) && (
                            <Paper sx={{ mt: 3 }}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <Tabs
                                        value={activeTabCategory}
                                        onChange={(e, newValue) => setActiveTabCategory(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        sx={{ px: 2 }}
                                    >
                                        {Array.from(new Set(evaluationResults.map(r => r.category))).map((category) => {
                                            const getCategoryIcon = (catName: string) => {
                                                switch (catName) {
                                                    case 'Presentation Structure': return <SchoolIcon />;
                                                    case 'Soft Skills': return <PsychologyIcon />;
                                                    case 'Solution Knowledge': return <BusinessIcon />;
                                                    default: return <BarChartIcon />;
                                                }
                                            };

                                            const categoryCriteria = criterionResults.filter(c => c.category === category);
                                            const hasData = categoryCriteria.length > 0 || evaluationResults.some(r => r.category === category);

                                            return (
                                                <Tab
                                                    key={category}
                                                    value={category}
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: 'column' }}>
                                                            {getCategoryIcon(category)}
                                                            <Typography variant="caption">{category}</Typography>
                                                            {hasData && (
                                                                <Chip
                                                                    size="small"
                                                                    label={categoryCriteria.length > 0 ? `${categoryCriteria.length} criteria` : 'Available'}
                                                                    color={categoryCriteria.length > 0 ? 'success' : 'info'}
                                                                    variant="outlined"
                                                                    sx={{ fontSize: '10px', height: 20 }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    disabled={!hasData}
                                                />
                                            );
                                        })}
                                    </Tabs>
                                </Box>

                                <Box sx={{ p: 3 }}>
                                    <Typography variant="h5" gutterBottom>
                                        {activeTabCategory} - Evaluation Results
                                    </Typography>

                                    {criterionResults.filter(c => c.category === activeTabCategory).length > 0 ? (
                                        <>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                Showing detailed evaluation results and coaching recommendations for {criterionResults.filter(c => c.category === activeTabCategory).length} criteria.
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {criterionResults.filter(c => c.category === activeTabCategory).map((criterion, index) => (
                                                    <Grid size={{ xs: 12 }} key={index}>
                                                        <Card variant="outlined">
                                                            <CardContent>
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                    <Typography variant="h6" fontWeight="bold">
                                                                        {criterion.criterionName}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={`${criterion.score || 0}/5`}
                                                                        color={getScoreColor(criterion.score || 0, 5) as any}
                                                                        size="small"
                                                                    />
                                                                </Box>

                                                                <ExpandableText
                                                                    text={criterion.feedback || 'No detailed feedback available'}
                                                                    maxLength={400}
                                                                    variant="body2"
                                                                    showReadMore={true}
                                                                />
                                                            </CardContent>
                                                        </Card>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </>
                                    ) : (
                                        <Alert severity="info" sx={{ textAlign: 'center' }}>
                                            No evaluation results available for {activeTabCategory}.
                                        </Alert>
                                    )}
                                </Box>
                            </Paper>
                        )}
                    </>
                )}
            </Box>
        );
    }

    // This condition should never be reached now due to the useEffect above
    if (!report && !hasEvaluationData && !loading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Evaluation Report Not Available
                    </Typography>
                    <Typography variant="body2">
                        The evaluation for this presentation is either not complete or the report hasn't been generated yet.
                        Please check back later or contact support if you believe this is an error.
                    </Typography>
                </Alert>
                <Button
                    variant="contained"
                    onClick={() => navigate(`/presentations/${presentationId}`)}
                    startIcon={<ArrowBackIcon />}
                >
                    Back to Presentation
                </Button>
            </Box>
        );
    }

    // Show the main report interface if we have any data (report or evaluation results)
    const shouldShowReport = report || hasEvaluationData;

    if (!shouldShowReport) {
        return null; // This should be handled by the error states above
    }

    const activeCategoryCriteria = criterionResults.filter(c => c.category === activeTabCategory);
    const activeCategoryResult = evaluationResults.find(r => r.category === activeTabCategory);

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {presentation?.description || 'Presentation Report'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {report?.generatedAt ?
                            `Generated on ${new Date((report.generatedAt as any).seconds * 1000).toLocaleDateString()}` :
                            'Evaluation Results'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Detailed Rubric">
                        <IconButton onClick={handleViewRubricDetails} color="primary">
                            <RubricIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy to Clipboard">
                        <IconButton onClick={handleCopyMarkdown}>
                            <CopyIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export to PDF">
                        <IconButton onClick={handleExportPDF}>
                            <PdfIcon />
                        </IconButton>
                    </Tooltip>
                    {isAdmin && (
                        <Tooltip title="Regenerate Report">
                            <IconButton onClick={handleRegenerateReport} disabled={regenerating}>
                                {regenerating ? <CircularProgress size={24} /> : <RefreshIcon />}
                            </IconButton>
                        </Tooltip>
                    )}
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/presentations/${presentationId}`)}
                        variant="outlined"
                        sx={{ ml: 2 }}
                    >
                        Back
                    </Button>
                </Box>
            </Box>

            {/* Score Overview */}
            <Box sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <ScoreCard
                            title="Overall Score"
                            score={report?.overallScore || calculateOverallScore(evaluationResults, criterionResults)}
                            maxScore={5}
                            color="primary"
                            icon={<StarIcon />}
                        />
                    </Grid>
                    {evaluationResults.map((result) => {
                        const getCategoryIcon = (category: string) => {
                            switch (category) {
                                case 'Presentation Structure': return <SchoolIcon />;
                                case 'Soft Skills': return <PsychologyIcon />;
                                case 'Solution Knowledge': return <BusinessIcon />;
                                default: return <BarChartIcon />;
                            }
                        };

                        // Prepare custom metrics for Speaker Analysis
                        let customMetrics: { label: string, value: string | number, subtext?: string, color?: string }[] | undefined = undefined;

                        if (result.category === 'Speaker Analysis' || result.key === 'speaker_analysis') {
                            const audioMetrics = (presentation as any)?.transcriptData?.audioMetrics;
                            if (audioMetrics) {
                                customMetrics = [
                                    {
                                        label: 'Pace',
                                        value: audioMetrics.wpm || 0,
                                        subtext: 'WPM',
                                        color: 'primary.main'
                                    },
                                    {
                                        label: 'Fillers',
                                        value: audioMetrics.fillerCount || 0,
                                        subtext: (audioMetrics.fillerCount || 0) > 10 ? 'High' : 'Good',
                                        color: (audioMetrics.fillerCount || 0) > 10 ? 'error.main' : 'success.main'
                                    },
                                    {
                                        label: 'Tone',
                                        value: audioMetrics.sentiment?.dominantSentiment || 'Mixed',
                                        color: 'info.main'
                                    }
                                ];
                            }
                        }

                        return (
                            <Grid size={{ xs: 12, md: 3 }} key={result.id}>
                                <ScoreCard
                                    title={result.category}
                                    score={result.overallScore}
                                    maxScore={5}
                                    color={getScoreColor(result.overallScore, 5)}
                                    icon={getCategoryIcon(result.category)}
                                    confidence={result.confidence}
                                    metrics={customMetrics}
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>

            {/* Detailed Evaluation (Schema/Framework) */}
            {(evaluationResults.length > 0 || criterionResults.length > 0) && (
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Detailed Evaluation (Framework)
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Category and criteria-level scores generated from the assigned evaluation framework.
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<RubricIcon />}
                            onClick={handleViewRubricDetails}
                        >
                            Open Rubric Detail View
                        </Button>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={activeTabCategory}
                            onChange={(e, newValue) => setActiveTabCategory(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ px: 1 }}
                        >
                            {evaluationResults.map((result) => {
                                const categoryCriteriaCount = criterionResults.filter(c => c.category === result.category).length;
                                return (
                                    <Tab
                                        key={result.id}
                                        value={result.category}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {result.category}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={`${categoryCriteriaCount} criteria`}
                                                    color={categoryCriteriaCount > 0 ? 'success' : 'default'}
                                                    variant="outlined"
                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                />
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </Tabs>
                    </Box>

                    <Box sx={{ pt: 3 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                                            {activeTabCategory}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Chip
                                                label={`Score: ${(activeCategoryResult?.overallScore ?? 0).toFixed(1)}/5`}
                                                color={getScoreColor(activeCategoryResult?.overallScore ?? 0, 5) as any}
                                                size="small"
                                            />
                                            {typeof activeCategoryResult?.confidence === 'number' && (
                                                <Chip
                                                    label={`Confidence: ${(activeCategoryResult.confidence * 100).toFixed(0)}%`}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                        <ExpandableText
                                            text={activeCategoryResult?.categoryFeedback || 'No category feedback available.'}
                                            maxLength={400}
                                            variant="body2"
                                            showReadMore={true}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid size={{ xs: 12, md: 8 }}>
                                {activeCategoryCriteria.length > 0 ? (
                                    <Box>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                                            Criteria Scores
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {activeCategoryCriteria.map((criterion, index) => (
                                                <Grid size={{ xs: 12 }} key={criterion.id || index}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                    {criterion.criterionName}
                                                                </Typography>
                                                                <Chip
                                                                    label={`${criterion.score || 0}/5`}
                                                                    color={getScoreColor(criterion.score || 0, 5) as any}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                            <ExpandableText
                                                                text={criterion.feedback || 'No detailed feedback available.'}
                                                                maxLength={500}
                                                                variant="body2"
                                                                showReadMore={true}
                                                            />

                                                            {(criterion.strengths?.length > 0 || criterion.areasForImprovement?.length > 0 || criterion.recommendations?.length > 0) && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    {criterion.strengths?.length > 0 && (
                                                                        <Accordion>
                                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                                <Typography sx={{ fontWeight: 600 }}>Strengths</Typography>
                                                                            </AccordionSummary>
                                                                            <AccordionDetails>
                                                                                <List dense>
                                                                                    {criterion.strengths.map((s, i) => (
                                                                                        <ListItem key={i} sx={{ py: 0 }}>
                                                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                                                <CheckIcon color="success" fontSize="small" />
                                                                                            </ListItemIcon>
                                                                                            <ListItemText primary={s} />
                                                                                        </ListItem>
                                                                                    ))}
                                                                                </List>
                                                                            </AccordionDetails>
                                                                        </Accordion>
                                                                    )}

                                                                    {criterion.areasForImprovement?.length > 0 && (
                                                                        <Accordion>
                                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                                <Typography sx={{ fontWeight: 600 }}>Areas for Improvement</Typography>
                                                                            </AccordionSummary>
                                                                            <AccordionDetails>
                                                                                <List dense>
                                                                                    {criterion.areasForImprovement.map((s, i) => (
                                                                                        <ListItem key={i} sx={{ py: 0 }}>
                                                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                                                <CancelIcon color="warning" fontSize="small" />
                                                                                            </ListItemIcon>
                                                                                            <ListItemText primary={s} />
                                                                                        </ListItem>
                                                                                    ))}
                                                                                </List>
                                                                            </AccordionDetails>
                                                                        </Accordion>
                                                                    )}

                                                                    {criterion.recommendations?.length > 0 && (
                                                                        <Accordion>
                                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                                <Typography sx={{ fontWeight: 600 }}>Recommendations</Typography>
                                                                            </AccordionSummary>
                                                                            <AccordionDetails>
                                                                                <List dense>
                                                                                    {criterion.recommendations.map((s, i) => (
                                                                                        <ListItem key={i} sx={{ py: 0 }}>
                                                                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                                                                <TrendingUpIcon color="info" fontSize="small" />
                                                                                            </ListItemIcon>
                                                                                            <ListItemText primary={s} />
                                                                                        </ListItem>
                                                                                    ))}
                                                                                </List>
                                                                            </AccordionDetails>
                                                                        </Accordion>
                                                                    )}
                                                                </Box>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        No criteria-level results available for {activeTabCategory}.
                                    </Alert>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            )}

            {/* Report Content */}
            <Paper sx={{ p: 4, mb: 4 }}>
                {report?.markdownContent ? (
                    <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'sans-serif' }}>
                        <Typography variant="body1">
                            {report.markdownContent}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body1" color="text.secondary" align="center">
                        {hasEvaluationData
                            ? "Full written report not available, but numerical scores are shown above. Use the Detailed Rubric view to see more specific feedback."
                            : "Report content loading..."}
                    </Typography>
                )}
            </Paper>

            {/* Copy Dialog */}
            <Dialog open={showCopyDialog} onClose={() => setShowCopyDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>Markdown Report Content</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            multiline
                            rows={15}
                            fullWidth
                            value={copyContent}
                            variant="outlined"
                            InputProps={{
                                readOnly: true,
                                style: { fontFamily: 'monospace', fontSize: '13px' }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCopyDialog(false)}>Close</Button>
                    <Button onClick={handleDownloadMarkdown} startIcon={<DownloadIcon />}>
                        Download .md
                    </Button>
                    <Button onClick={handleCopyToClipboard} variant="contained" startIcon={<CopyIcon />}>
                        Copy to Clipboard
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReportViewer;
